// src/components/admin/DialogHistorialAuditoria.js
//
// D-27 — "¿Qué le hicieron a esto?"
//
// Se abre desde la fila de un usuario, un negocio o un secreto y muestra en
// orden todo lo que se le hizo: quién, cuándo, qué y con qué resultado. Es
// la vista que se necesita cuando alguien reclama, y por eso se llega a ella
// desde la tabla donde está el afectado, no desde un panel aparte.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Stack, Alert, LinearProgress, Chip, Accordion, AccordionSummary, AccordionDetails, Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { admin } from '../../api/authApi';
import VistaCambioAuditoria from './VistaCambioAuditoria';

const fechaCo = (iso) => {
  if (!iso) return '—';
  const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString('es-CO', {
    timeZone: 'America/Bogota', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const chipResultado = (r) => {
  if (r === 'OK') return <Chip size="small" color="success" label="OK" />;
  if (r === 'ERROR_CLIENTE') return <Chip size="small" color="warning" label="Rechazado" />;
  return <Chip size="small" color="error" label={r === 'EXCEPCION' ? 'Falló' : 'Error' } />;
};

/**
 * @param {object}   props
 * @param {boolean}  props.abierto
 * @param {string}   props.entidad    'Usuario' | 'Negocio' | 'Secreto' | …
 * @param {string|number} props.entidadId
 * @param {string}   props.titulo     nombre legible del afectado
 * @param {Function} props.onCerrar
 */
export default function DialogHistorialAuditoria({ abierto, entidad, entidadId, titulo, onCerrar }) {
  const [items, setItems]     = useState([]);
  const [cargando, setCarga]  = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!abierto || !entidad || entidadId == null) return;
    let vivo = true;
    (async () => {
      setCarga(true); setError('');
      try {
        const r = await admin.auditoriaPorEntidad(entidad, entidadId, 200);
        if (vivo) setItems(r?.items || []);
      } catch (e) {
        if (vivo) setError(e.mensajeUsuario || 'No se pudo cargar el historial.');
      } finally {
        if (vivo) setCarga(false);
      }
    })();
    return () => { vivo = false; };
  }, [abierto, entidad, entidadId]);

  return (
    <Dialog open={!!abierto} onClose={onCerrar} maxWidth="md" fullWidth>
      <DialogTitle>
        Historial de auditoría
        <Typography variant="body2" color="text.secondary">
          {titulo || `${entidad} ${entidadId}`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {cargando && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!cargando && !error && items.length === 0 && (
          <Alert severity="info">
            No hay acciones registradas sobre este registro. La auditoría empezó
            a guardar el {'\u2060'}día que se instaló D-27: lo anterior a eso no
            quedó.
          </Alert>
        )}

        <Stack spacing={1}>
          {items.map((it) => (
            <Accordion key={it.id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" variant="outlined" label={it.accion} />
                    {chipResultado(it.resultado)}
                    <Typography variant="caption" color="text.secondary">
                      {fechaCo(it.fecha)} · {it.actor || 'anónimo'}
                    </Typography>
                  </Box>
                  {it.descripcion && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{it.descripcion}</Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <VistaCambioAuditoria antes={it.antes} despues={it.despues} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
